import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ApiResponsePanel = ({ deliveryLogs }) => {
  const [selectedLog, setSelectedLog] = useState(null);

  const logsWithResponses = deliveryLogs?.filter(log => 
    log?.notification_delivery_log?.[0] || log?.error_message
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <Icon name="Code" size={24} className="text-blue-600 dark:text-blue-400" />
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Resend API Response Details</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">View detailed API responses, error codes, and debugging information</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Log List */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Recent API Calls</h4>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {logsWithResponses?.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No API responses available</p>
            ) : (
              logsWithResponses?.map((log) => (
                <button
                  key={log?.id}
                  onClick={() => setSelectedLog(log)}
                  className={`w-full text-left p-4 rounded-lg border transition-colors ${
                    selectedLog?.id === log?.id
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' :'border-neutral-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-700'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {log?.recipient_email}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      log?.notification_status === 'delivered' ?'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                        : log?.notification_status === 'failed' ?'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400' :'bg-gray-100 text-gray-700 dark:bg-gray-900/20 dark:text-gray-400'
                    }`}>
                      {log?.notification_status}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {new Date(log?.created_at)?.toLocaleString()}
                  </p>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Response Details */}
        <div>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">API Response</h4>
          {!selectedLog ? (
            <div className="flex items-center justify-center h-64 bg-gray-50 dark:bg-slate-700 rounded-lg border border-neutral-200 dark:border-slate-600">
              <p className="text-sm text-gray-500 dark:text-gray-400">Select a log to view API response</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Response Summary */}
              <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-4 border border-neutral-200 dark:border-slate-600">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Status</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {selectedLog?.notification_status || 'Unknown'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Retry Count</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {selectedLog?.retry_count || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Delivery Channel</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {selectedLog?.notification_delivery_log?.[0]?.delivery_channel || 'email'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Created At</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {new Date(selectedLog?.created_at)?.toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Error Details */}
              {selectedLog?.error_message && (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border border-red-200 dark:border-red-800">
                  <p className="text-xs font-semibold text-red-900 dark:text-red-200 mb-2">Error Message</p>
                  <p className="text-sm text-red-700 dark:text-red-300">{selectedLog?.error_message}</p>
                </div>
              )}

              {/* Full Response JSON */}
              <div>
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Full Response Data</p>
                <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-xs overflow-x-auto max-h-96 overflow-y-auto">
                  {JSON.stringify({
                    id: selectedLog?.id,
                    recipient_email: selectedLog?.recipient_email,
                    subject: selectedLog?.subject,
                    notification_status: selectedLog?.notification_status,
                    notification_type: selectedLog?.notification_type,
                    created_at: selectedLog?.created_at,
                    sent_at: selectedLog?.sent_at,
                    delivered_at: selectedLog?.delivered_at,
                    retry_count: selectedLog?.retry_count,
                    error_message: selectedLog?.error_message,
                    delivery_log: selectedLog?.notification_delivery_log?.[0] || null
                  }, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApiResponsePanel;